import { useNavigate } from 'react-router-dom';
import { useRaffleSummary, useRaffles, useScrapers, useSystemSummary } from '../../api/queries';
import { Ic, type IconName } from '../../components/Ic';
import { Spark } from '../../components/Spark';
import { Gauge } from '../../components/Gauge';
import { StatusDot } from '../../components/StatusDot';
import { RaffleRow } from '../../components/RaffleRow';
import { Empty } from '../../components/Empty';
import { deriveScraperState, isAttentionWorthy, STATE_META } from '../../lib/scraperState';
import { formatInterval, relTime } from '../../lib/time';
import { AlertFeedCard } from './AlertFeedCard';
import { useReconcileScraperFleet } from '../../api/mutations';
import { useMe } from '../../api/queries';

function Kpi({
  label,
  value,
  sub,
  icon,
  color,
  spark,
  sparkColor,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: IconName;
  color?: 'green' | 'red' | 'yellow' | 'indigo';
  spark?: number[];
  sparkColor?: string;
}) {
  const Icon = Ic[icon];
  return (
    <div className="kpi">
      <div className="top">
        <Icon className="ic" />
        <span className="label">{label}</span>
      </div>
      <div className={`value ${color ?? ''}`}>{value}</div>
      {sub && <div className="delta">{sub}</div>}
      {spark && spark.length > 0 && (
        <div className="spark">
          <Spark data={spark} color={sparkColor ?? 'var(--accent)'} w={150} h={26} />
        </div>
      )}
    </div>
  );
}

export function OverviewView() {
  const navigate = useNavigate();
  const { data: scrapers } = useScrapers();
  const { data: summary } = useSystemSummary();
  const { data: hourly } = useRaffleSummary(12);
  const { data: recent, error: recentError } = useRaffles({
    limit: 8,
    sort: 'detected',
    order: 'desc',
  });
  const { data: live } = useRaffles({ status: 'posted', active: true, limit: 1 });
  const { data: me } = useMe();
  const reconcile = useReconcileScraperFleet();

  const attention = scrapers?.filter(isAttentionWorthy) ?? [];
  const total = scrapers?.length ?? 0;
  const healthy = scrapers?.filter((s) => deriveScraperState(s) === 'healthy').length ?? 0;
  const paused = scrapers?.filter((s) => !s.enabled).length ?? 0;

  const topSites = Object.entries(hourly?.by_site ?? {})
    .filter(([, v]) => v.today > 0)
    .sort((a, b) => b[1].today - a[1].today)
    .slice(0, 5);
  const topMax = Math.max(1, ...topSites.map(([, v]) => v.today));

  const worker = summary?.worker;
  const offlineRunners = (scrapers ?? []).filter((s) => s.enabled && s.runner_alive === false);
  // Only fleet mode has per-scraper runners; otherwise the tile has nothing true to say.
  const fleetMode = (scrapers ?? []).some((s) => s.runner_alive !== null);

  return (
    <div className="view-pad">
      <header className="page-head">
        <div>
          <h1>Ops</h1>
          <p className="page-sub">Engine health, runner fleet and delivery at a glance</p>
        </div>
      </header>
      {attention.length > 0 && (
        <div className="pcard tone-err" style={{ marginBottom: 16 }}>
          <div className="banner">
            <Ic.zap className="ic" />
            <div className="main">
              <div className="t">
                {attention.length} scraper{attention.length > 1 ? 's' : ''} need attention
              </div>
              <div className="m">{attention.map((s) => s.name).join(', ')}</div>
            </div>
            <button className="btn sm" onClick={() => navigate('/scrapers')}>
              View scrapers <Ic.arrowRight className="ico" />
            </button>
          </div>
        </div>
      )}

      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <Kpi
          label="Live Raffles"
          value={live?.total ?? '—'}
          icon="tag"
          color="green"
          sub="posted and still open"
        />
        <Kpi
          label="Sent Today"
          value={summary?.sent_today ?? '—'}
          icon="activity"
          spark={hourly?.hourly}
          sparkColor="var(--accent)"
          sub="last 24 hours"
        />
        <Kpi
          label="Scrapers Up"
          value={total ? `${healthy}/${total}` : '—'}
          icon="box"
          color={total > 0 && healthy === total ? 'green' : 'yellow'}
          sub={`${total - paused} active · ${paused} paused`}
        />
        {/* The design's "Deliveries Today" duplicated sent_today. Worker health
            is real, currently invisible in the UI, and the thing you actually
            need to know when numbers look wrong. */}
        <Kpi
          label="Worker"
          value={worker ? (worker.alive ? 'alive' : 'down') : '—'}
          icon="cmd"
          color={worker?.alive ? 'green' : 'red'}
          sub={
            worker?.alive
              ? `${worker.jobs ?? '?'} jobs · heartbeat ${Math.round(worker.age_seconds ?? 0)}s ago`
              : 'no recent heartbeat'
          }
        />
        {fleetMode && <Kpi
          label="Runner fleet"
          value={scrapers ? `${total - offlineRunners.length}/${total}` : '—'}
          icon="box"
          color={offlineRunners.length ? 'red' : 'green'}
          sub={offlineRunners.length ? `${offlineRunners.length} enabled runner${offlineRunners.length > 1 ? 's' : ''} offline` : 'all enabled runners reporting'}
        />}
      </div>

      {offlineRunners.length > 0 && (
        <div className="pcard tone-warn" style={{ marginBottom: 16 }}>
          <div className="banner">
            <Ic.alert className="ic" />
            <div className="main"><div className="t">Runner reconciliation needed</div><div className="m">{offlineRunners.map((s) => s.name).join(', ')}</div></div>
            {me?.is_super_admin && <button className="btn sm" onClick={() => reconcile.mutate()} disabled={reconcile.isPending}>Reconcile fleet</button>}
          </div>
        </div>
      )}

      <div className="sec-head">
        <h2>Scraper health</h2>
        <span className="desc">live status across all scrapers</span>
        <span className="spacer" />
        <button className="btn ghost sm" onClick={() => navigate('/scrapers')}>
          Open monitor <Ic.arrowRight className="ico" />
        </button>
      </div>
      <div className="health-strip" style={{ marginBottom: 24 }}>
        {scrapers?.map((s) => {
          const state = deriveScraperState(s);
          return (
            <button
              key={s.name}
              className="hchip"
              onClick={() => navigate(`/scrapers/${encodeURIComponent(s.name)}`)}
            >
              <div className="row1">
                <StatusDot state={state} />
                <span className="name">{s.name}</span>
              </div>
              <div className="site">every {formatInterval(s.interval_seconds)}</div>
              <div className="row2">
                {/* The design showed uptime %, which nothing tracks. health_score
                    is the real signal the backend computes. */}
                <span className="up" style={{ color: STATE_META[state].dot }}>
                  {s.health_score}
                </span>
                <span className="lbl">{state === 'paused' ? 'paused' : 'health'}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="two-col">
        <div className="pcard">
          <div className="head">
            <Ic.history className="ic" />
            <h3>Recently detected</h3>
            <span className="spacer" />
            <span className="pill">
              <span className="live-dot" style={{ marginRight: 2 }} />
              live
            </span>
          </div>
          <div className="body">
            {recentError ? (
              // Not the same as "no raffles yet" — saying that when the API is
              // down would read as data loss.
              <Empty
                icon={Ic.alert}
                title="Could not load raffles"
                detail={recentError instanceof Error ? recentError.message : String(recentError)}
              />
            ) : recent?.items.length ? (
              recent.items.map((r) => (
                <RaffleRow key={r.id} raffle={r} onClick={() => navigate(`/raffles/${r.id}`)} />
              ))
            ) : (
              <Empty
                icon={Ic.database}
                title="No raffles yet"
                detail="They appear here as scrapers find them."
              />
            )}
          </div>
        </div>

        <div className="stack">
          <div className="pcard">
            <div className="head">
              <Ic.trend className="ic" />
              <h3>Top sources today</h3>
            </div>
            <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {topSites.length ? (
                topSites.map(([site, stats]) => (
                  <div key={site} className="src-row">
                    <span className="src-name" title={site}>{site}</span>
                    <Gauge value={stats.today} max={topMax} color="var(--accent)" />
                    <span className="src-n">{stats.today}</span>
                  </div>
                ))
              ) : (
                <div className="muted" style={{ fontSize: 12.5 }}>
                  Nothing posted in the last 24 hours.
                </div>
              )}
            </div>
          </div>

          <AlertFeedCard />
        </div>
      </div>

      {summary?.last_deploy && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 18, textAlign: 'right' }}>
          build {summary.commit} · last deploy {relTime(summary.last_deploy.ts)}
        </div>
      )}
    </div>
  );
}
